import type { CaptureSession, WorkflowType } from '@/shared/capture/capture-session';

export const FIELD_DATABASE_NAME = 'enmon-inventory-field-db-v2';
export const FIELD_DATABASE_VERSION = 1;
export const fieldStoreNames = [
  'location_bootstraps',
  'meters',
  'capture_sessions',
  'sync_queue',
  'photo_blobs',
  'floorplan_blobs',
] as const;
export type FieldStoreName = (typeof fieldStoreNames)[number];

export function getMissingFieldStores(existingStoreNames: Iterable<string>) {
  const existing = new Set(existingStoreNames);

  return fieldStoreNames.filter(storeName => !existing.has(storeName));
}

export interface CachedEntityRecord {
  id: string;
  remoteId?: string | number;
  updatedAt: string;
  data: Record<string, unknown>;
}

export const SYNC_QUEUE_STATUSES = {
  queued: 'queued',
  syncing: 'syncing',
  synced: 'synced',
  syncFailed: 'sync_failed',
  conflict: 'conflict',
  needsReview: 'needs_review',
} as const;

export type SyncQueueStatus = (typeof SYNC_QUEUE_STATUSES)[keyof typeof SYNC_QUEUE_STATUSES];

export interface SyncQueueRecord {
  id: string;
  sessionId: string;
  workflowType: WorkflowType;
  status: SyncQueueStatus;
  idempotencyKey: string;
  locationApiKey: string;
  locationId?: number | string;
  createdAt: string;
  updatedAt: string;
  lastErrorCode?: string;
}

export interface PhotoBlobRecord {
  id: string;
  blob: Blob;
  createdAt: string;
  mimeType: string;
  sizeBytes: number;
}

export const FLOORPLAN_BLOB_SOURCES = {
  local: 'local',
  remote: 'remote',
} as const;
export type FloorplanBlobSource = (typeof FLOORPLAN_BLOB_SOURCES)[keyof typeof FLOORPLAN_BLOB_SOURCES];

export interface FloorplanBlobRecord {
  blob: Blob;
  buildingFloor: string;
  buildingId: number | string | null;
  floorplanId: string;
  height?: number;
  id: string;
  loadedAt: string;
  locationId: number | string;
  mimeType?: string;
  name?: string;
  pendingUpload?: boolean;
  source?: FloorplanBlobSource;
  uploadedAt?: string;
  width?: number;
}

export interface LocationBootstrapRecord {
  id: string;
  apiKeyFingerprint: string;
  data: {
    buildings: unknown[];
    existingMeters: unknown[];
    location: unknown;
    manufacturers: Array<{ id: number | string; name: string }>;
    meterModels: unknown[];
    ownerSuggestions: string[];
    tenants: string[];
    thirdParties: unknown[];
  };
  loadedAt: string;
  locationId: number | string;
}

export interface FieldStoreRecords {
  location_bootstraps: LocationBootstrapRecord;
  meters: CachedEntityRecord;
  capture_sessions: CaptureSession;
  photo_blobs: PhotoBlobRecord;
  floorplan_blobs: FloorplanBlobRecord;
  sync_queue: SyncQueueRecord;
}

export type FieldRecord<K extends FieldStoreName> = FieldStoreRecords[K];

export interface FieldDatabase {
  get<K extends FieldStoreName>(storeName: K, id: string): Promise<FieldRecord<K> | undefined>;
  getAll<K extends FieldStoreName>(storeName: K): Promise<Array<FieldRecord<K>>>;
  put<K extends FieldStoreName>(storeName: K, record: FieldRecord<K>): Promise<void>;
  delete(storeName: FieldStoreName, id: string): Promise<void>;
  clear(storeName: FieldStoreName): Promise<void>;
  count(storeName: FieldStoreName): Promise<number>;
}
